import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getApiUrl } from '../../../config/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/dialog';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import { Button } from '@/Components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import { Card, CardContent } from '@/Components/ui/card';
import {
  Download,
  Eye,
  Loader2,
  Lock,
  Pencil,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { PlanLockCard } from '../../Components/PlanFeatureLock';
import { OfferApprovalNote, OfferReleaseActions, approvalAllowsRelease, useOfferApproval } from './OfferApproval';
import { getApprovalState } from './offerApprovalsApi';

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship'];
const WORK_ARRANGEMENTS = ['On-site', 'Hybrid', 'Remote'];
const SALUTATIONS = ['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Mx.'];
const TEMPLATE_CATEGORIES = ['General', 'Full-time', 'Part-time', 'Contract', 'Internship', 'Executive'];
const CURRENCIES = [
  { value: 'INR', label: 'Indian Rupee (INR)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'AED', label: 'UAE Dirham (AED)' },
  { value: 'AUD', label: 'Australian Dollar (AUD)' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
  { value: 'CHF', label: 'Swiss Franc (CHF)' },
  { value: 'CNY', label: 'Chinese Yuan (CNY)' },
  { value: 'HKD', label: 'Hong Kong Dollar (HKD)' },
  { value: 'JPY', label: 'Japanese Yen (JPY)' },
  { value: 'KWD', label: 'Kuwaiti Dinar (KWD)' },
  { value: 'QAR', label: 'Qatari Riyal (QAR)' },
  { value: 'SAR', label: 'Saudi Riyal (SAR)' },
  { value: 'SGD', label: 'Singapore Dollar (SGD)' },
];
const SYSTEM_TEMPLATE_SUMMARIES = {
  standard_full_time: 'For full-time employment.',
  contract: 'For contract duration and end date.',
  part_time: 'For part-time hours and work schedule.',
  internship: 'For internship duration and stipend.',
  executive: 'For executive employment.',
};

const SYSTEM_TEMPLATE_FALLBACK = [
  { id: 'system:standard_full_time', type: 'system', key: 'standard_full_time', version: 1, name: 'Full-time Offer', category: 'Full-time', description: 'Professional permanent-employment offer letter.', recommended: true, fields: [] },
  { id: 'system:contract', type: 'system', key: 'contract', version: 1, name: 'Contract Offer', category: 'Contract', description: 'Fixed-term employment offer with contract duration details.', fields: [{ key: 'contract_duration', label: 'Contract Duration', type: 'text', placeholder: 'For example: 12 months', required: true }, { key: 'contract_end_date', label: 'Contract End Date', type: 'date', required: true }, { key: 'contract_renewal_terms', label: 'Renewal Terms', type: 'text', placeholder: 'For example: Renewable by mutual written agreement', required: false }, { key: 'contract_termination_notice', label: 'Early Termination Notice', type: 'text', placeholder: "For example: 30 days' written notice", required: true }] },
  { id: 'system:part_time', type: 'system', key: 'part_time', version: 1, name: 'Part-time Offer', category: 'Part-time', description: 'Part-time offer with weekly working hours and schedule details.', fields: [{ key: 'part_time_weekly_hours', label: 'Weekly Working Hours', type: 'number', min: '1', max: '48', step: '0.5', placeholder: 'For example: 20', required: true }, { key: 'part_time_schedule', label: 'Work Schedule', type: 'text', placeholder: 'For example: Monday to Friday, 10 AM to 2 PM', required: true, max_length: 250 }] },
  { id: 'system:internship', type: 'system', key: 'internship', version: 1, name: 'Internship Offer', category: 'Internship', description: 'Internship offer with duration and monthly stipend details.', fields: [{ key: 'internship_duration', label: 'Internship Duration', type: 'text', placeholder: 'For example: 6 months', required: true }, { key: 'monthly_stipend', label: 'Monthly Stipend', type: 'number', min: '0.01', step: '0.01', required: true }, { key: 'internship_mentor', label: 'Mentor / Supervisor', type: 'text', placeholder: 'Mentor or supervisor name', required: false }, { key: 'internship_conversion_policy', label: 'Post-internship Outcome', type: 'text', placeholder: 'For example: No guarantee of regular employment', required: false }] },
  { id: 'system:executive', type: 'system', key: 'executive', version: 1, name: 'Executive Offer', category: 'Executive', description: 'Senior leadership offer with executive appointment and compensation details.', fields: [{ key: 'executive_reporting_to', label: 'Executive Reports To', type: 'text', placeholder: 'For example: Chief Executive Officer or Board of Directors', required: true }, { key: 'executive_variable_pay', label: 'Annual Variable Pay', type: 'number', min: '0', step: '0.01', required: false }, { key: 'executive_equity_details', label: 'Equity / Long-term Incentive', type: 'text', placeholder: 'For example: ESOPs as per the approved plan', required: false }] },
];

const TEMPLATE_FIELDS = {
  candidate_name: { label: 'Candidate Name', group: 'Candidate and Job Details' },
  candidate_address: { label: 'Candidate Address', group: 'Candidate and Job Details', multiline: true },
  candidate_email: { label: 'Candidate Email', group: 'Candidate and Job Details', type: 'email' },
  candidate_phone: { label: 'Candidate Phone', group: 'Candidate and Job Details', type: 'tel' },
  job_title: { label: 'Job Title', group: 'Candidate and Job Details' },
  department: { label: 'Department', group: 'Candidate and Job Details' },
  employment_type: { label: 'Employment Type', group: 'Employment Details', select: EMPLOYMENT_TYPES },
  work_location: { label: 'Work Location', group: 'Employment Details' },
  joining_date: { label: 'Joining Date', group: 'Employment Details', type: 'date' },
  offer_date: { label: 'Offer Date', group: 'Employment Details', type: 'date' },
  offer_expiry_date: { label: 'Offer Expiry Date', group: 'Employment Details', type: 'date' },
  reporting_manager: { label: 'Reporting Manager', group: 'Employment Details' },
  probation_period: { label: 'Probation Period', group: 'Additional Terms' },
  annual_ctc: { label: 'Annual CTC', group: 'Compensation Details', type: 'number', min: '0.01', step: '0.01' },
  salary_breakdown: { label: 'Salary Breakdown', group: 'Compensation Details', multiline: true },
  notice_period: { label: 'Notice Period', group: 'Additional Terms' },
  working_hours: { label: 'Working Hours', group: 'Additional Terms' },
  company_name: { label: 'Company Name', group: 'Candidate and Job Details' },
  company_address: { label: 'Company Address', group: 'Candidate and Job Details', multiline: true },
  company_email: { label: 'Company Email', group: 'Candidate and Job Details', type: 'email' },
  company_contact_number: { label: 'Company Contact Number', group: 'Candidate and Job Details', type: 'tel' },
  authorized_signatory: { label: 'Authorized Signatory', group: 'Additional Terms' },
  signatory_designation: { label: 'Signatory Designation', group: 'Additional Terms' },
  authorized_person_name: { label: 'Authorized Person Name', group: 'Additional Terms' },
  authorized_person_designation: { label: 'Authorized Person Designation', group: 'Additional Terms' },
};

const GROUPS = ['Candidate and Job Details', 'Compensation Details', 'Employment Details', 'Additional Terms'];
// These values are collected in the common Offer Details form for every
// template. A company template may map them to its own layout, but it should
// not show a second, duplicate input for the same offer value.
const COMMON_OFFER_TEMPLATE_FIELDS = new Set([
  'offer_date', 'offer_expiry_date', 'joining_date', 'employment_type',
  'work_location', 'reporting_manager', 'probation_period', 'annual_ctc',
  'authorized_signatory', 'signatory_designation', 'authorized_person_name',
  'authorized_person_designation',
]);
const toInputValue = (value) => (value == null ? '' : String(value));
const toFilenamePart = (value, fallback) => {
  const safeValue = toInputValue(value)
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return (safeValue || fallback).slice(0, 80);
};
const buildOfferPdfFilename = (form, context) => {
  const templateFields = form?.template_fields || {};
  return [
    toFilenamePart(templateFields.candidate_name || context?.candidate?.name, 'Candidate'),
    'Offer',
    toFilenamePart(templateFields.job_title || context?.job?.title, 'Role'),
    toFilenamePart(templateFields.company_name || context?.company?.name, 'Company'),
  ].join('_') + '.pdf';
};
const toDateInputValue = (value) => {
  const normalized = toInputValue(value);
  const match = normalized.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : normalized;
};

const emptyForm = () => ({
  employment: {
    joining_date: '', work_location: '', employment_type: '', work_arrangement: '',
    working_hours: '', probation_period: '', notice_period: '', reporting_manager: '',
    offer_expiry_date: '', annual_leave_days: '', legal_jurisdiction: '',
  },
  compensation: {
    currency: 'INR', basic_salary: '', hra: '', special_allowance: '', variable_pay: '',
    other_allowances: '', gross_annual_salary: '', total_ctc: '',
  },
  benefits: { leave_benefit_description: '', health_insurance: '', equipment_benefit: '', additional_benefits: '' },
  signatory: { name: '', designation: '' },
  template_fields: {},
});

const formForContext = (context) => {
  const form = emptyForm();
  const company = context?.company || {};
  const job = context?.job || {};
  const candidate = context?.candidate || {};
  const offerDate = context?.offer_letter_date || '';
  form.employment.work_location = toInputValue(job.location);
  form.signatory.name = toInputValue(company.authorized_signatory_name);
  form.signatory.designation = toInputValue(company.authorized_signatory_designation);
  form.template_fields = {
    candidate_name: toInputValue(candidate.name),
    candidate_address: toInputValue(candidate.location),
    candidate_email: toInputValue(candidate.email),
    candidate_phone: toInputValue(candidate.phone),
    job_title: toInputValue(job.title),
    department: toInputValue(job.department),
    employment_type: '',
    work_location: '',
    joining_date: '',
    offer_date: toDateInputValue(offerDate),
    offer_expiry_date: '',
    reporting_manager: '',
    probation_period: '',
    annual_ctc: '',
    salary_breakdown: '',
    notice_period: '',
    working_hours: '',
    company_name: toInputValue(company.name),
    company_address: toInputValue(company.address),
    company_email: toInputValue(company.email),
    company_contact_number: toInputValue(company.phone),
    authorized_signatory: '',
    signatory_designation: '',
    authorized_person_name: '',
    authorized_person_designation: '',
  };
  return form;
};

// Rebuild the editor form from a previous offer snapshot (used when revising a
// released/accepted offer). The snapshot's sections map 1:1 to the form, and
// it preserves template_fields verbatim, so HR only tweaks what changed.
const definedStrings = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') out[key] = String(value);
  });
  return out;
};
const formFromSnapshot = (base, snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return base;
  const employment = definedStrings(snapshot.employment);
  ['joining_date', 'offer_expiry_date'].forEach((k) => { if (employment[k]) employment[k] = toDateInputValue(employment[k]); });
  const templateFields = definedStrings(snapshot.template_fields);
  ['offer_date', 'joining_date', 'offer_expiry_date'].forEach((k) => { if (templateFields[k]) templateFields[k] = toDateInputValue(templateFields[k]); });
  return {
    employment: { ...base.employment, ...employment },
    compensation: { ...base.compensation, ...definedStrings(snapshot.compensation) },
    benefits: { ...base.benefits, ...definedStrings(snapshot.benefits) },
    signatory: { ...base.signatory, ...definedStrings(snapshot.signatory) },
    template_fields: { ...base.template_fields, ...templateFields },
  };
};

const fieldError = (errors, key) => errors[key] || '';
function ErrorText({ message }) { return message ? <p className="text-xs text-destructive">{message}</p> : null; }

function ReadOnlyValue({ label, value }) {
  return (
    <div className="min-w-0 rounded-md border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="min-h-5 break-words text-sm font-medium">{value || '—'}</p>
    </div>
  );
}

export default function OfferModal({ open, candidate, onClose, onReleased, prefill = null }) {
  const [context, setContext] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [systemTemplates, setSystemTemplates] = useState(SYSTEM_TEMPLATE_FALLBACK);
  const [templates, setTemplates] = useState([]);
  const [templateChoice, setTemplateChoice] = useState('system:standard_full_time');
  const [templateSource, setTemplateSource] = useState('system');
  const [activeStep, setActiveStep] = useState(1);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const [operation, setOperation] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [draftReady, setDraftReady] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewKind, setPreviewKind] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('General');
  const templateFileInputRef = useRef(null);
  // Retained only so older static PDF template records remain readable. New
  // uploads are rejected by the backend unless they contain named PDF fields.
  const [staticPdfMappings, setStaticPdfMappings] = useState([]);
  const [mappingDraft, setMappingDraft] = useState({ field: 'candidate_name', page: '1', left_mm: '25', top_mm: '25', width_mm: '80', height_mm: '8', font_size: '10' });
  // Plan entitlement: is offer-letter generation included in this company's plan?
  const [offerLocked, setOfferLocked] = useState(false);
  const [planName, setPlanName] = useState('');
  const [offerCost, setOfferCost] = useState(null);
  // The company has switched offer approvals on, so a saved draft is never saved again unchanged.
  const [approvalsOn, setApprovalsOn] = useState(false);

  const applicationId = candidate?.applicationId;
  const generatedOfferId = applicationId ? `SJAI-${String(applicationId).padStart(6, '0')}` : '';
  const busy = Boolean(operation);
  const [approval, refreshApproval] = useOfferApproval(applicationId, open && activeStep === 3 && draftReady);
  const company = context?.company || {};
  const candidateDetails = context?.candidate || {};
  const job = context?.job || {};
  const selectedTemplate = useMemo(() => {
    const selected = [...systemTemplates, ...templates]
      .find((template) => String(template.id) === String(templateChoice));
    if (selected) return selected;
    return templateSource === 'system' ? (systemTemplates[0] || SYSTEM_TEMPLATE_FALLBACK[0]) : null;
  }, [templateChoice, systemTemplates, templates, templateSource]);
  const isCompanyTemplate = templateSource === 'company'
    && Boolean(selectedTemplate && (selectedTemplate.type === 'company' || selectedTemplate.template_type));
  const isSystemTemplate = templateSource === 'system' && Boolean(selectedTemplate);
  const hasSelectedTemplate = isSystemTemplate || isCompanyTemplate;
  const selectedTemplateSummary = isSystemTemplate
    ? (SYSTEM_TEMPLATE_SUMMARIES[selectedTemplate?.key] || selectedTemplate?.description || '')
    : '';
  const templatePlaceholders = isCompanyTemplate ? (selectedTemplate.placeholders || []) : [];
  const systemTemplateFields = isSystemTemplate ? (selectedTemplate.fields || []) : [];
  const staticPdfBlock = useMemo(() => (
    isCompanyTemplate && selectedTemplate?.template_type === 'pdf'
      ? (selectedTemplate.blocks || []).find((block) => block?.type === 'static_pdf') || null
      : null
  ), [isCompanyTemplate, selectedTemplate]);
  const isStaticPdfTemplate = Boolean(staticPdfBlock);
  const showLegacyStaticPdfMapping = false;
  const staticPdfPages = staticPdfBlock?.pages || [];
  const canGenerateSelectedTemplate = hasSelectedTemplate && (!isStaticPdfTemplate || templatePlaceholders.length > 0);
  const remainingStaticPdfFields = Object.keys(TEMPLATE_FIELDS).filter(
    (field) => !staticPdfMappings.some((mapping) => mapping.field === field),
  );
  const missingProfileFields = [
    ['candidate_name', candidateDetails.name],
    ['candidate_email', candidateDetails.email],
    ['candidate_phone', candidateDetails.phone],
    ['candidate_address', candidateDetails.location],
    ['job_title', job.title],
    ['department', job.department],
    ['company_name', company.name],
    ['company_address', company.address],
    ['company_email', company.email],
    ['company_contact_number', company.phone],
  ].filter(([, value]) => !toInputValue(value).trim()).map(([field]) => field);

  const clearPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setPreviewKind('');
  };

  const clearError = (key) => setFieldErrors((current) => {
    if (!current[key]) return current;
    const next = { ...current };
    delete next[key];
    return next;
  });

  const markDraftChanged = () => {
    if (draftReady) setDraftReady(false);
    if (previewKind === 'offer') clearPreview();
  };

  const resetTemplateFile = () => {
    setTemplateFile(null);
    if (templateFileInputRef.current) templateFileInputRef.current.value = '';
  };

  const handleTemplateFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setTemplateFile(file);
    if (file) {
      setFieldErrors({});
      clearPreview();
      setDraftReady(false);
    }
  };

  useEffect(() => {
    if (!open || !applicationId) return undefined;
    let active = true;
    const load = async () => {
      setLoadingContext(true);
      setFieldErrors({});
      setDraftReady(false);
      clearPreview();
      try {
        const [contextResponse, templatesResponse] = await Promise.all([
          fetch(getApiUrl(`/api/hiring-lifecycle/applications/${applicationId}/offer-context`), { credentials: 'include' }),
          fetch(getApiUrl('/api/hiring-lifecycle/offer-templates'), { credentials: 'include' }),
        ]);
        const contextData = await contextResponse.json();
        const templatesData = await templatesResponse.json();
        if (!contextResponse.ok) throw new Error(contextData.error || 'Could not load offer details.');
        if (!templatesResponse.ok) throw new Error(templatesData.error || 'Could not load company templates.');
        if (!active) return;
        const nextContext = { ...(contextData.context || {}), offer_letter_date: contextData.offer_letter_date };
        setContext(nextContext);
        const baseForm = formForContext(nextContext);
        setForm(prefill ? formFromSnapshot(baseForm, prefill) : baseForm);
        setSystemTemplates(templatesData.system_templates?.length ? templatesData.system_templates : SYSTEM_TEMPLATE_FALLBACK);
        setTemplates(templatesData.templates || []);
        setTemplateChoice('system:standard_full_time');
        setTemplateSource('system');
        setActiveStep(prefill ? 2 : 1);
        setShowTemplateManager(false);
        setTemplateCategory('General');

        // With approvals on, a saved draft opens where it stands: saving it again would
        // make a new letter, and a new letter needs approving again.
        const saved = await getApprovalState(applicationId).catch(() => null);
        if (!active) return;
        setApprovalsOn(Boolean(saved?.required));
        if (saved?.required && saved.offer && !prefill) {
          try {
            await openPdfPreview(`/api/hiring-lifecycle/applications/${applicationId}/offer-draft`, 'offer');
          } catch {
            return; // The saved letter can't be shown, so the offer starts again from the template.
          }
          if (!active) return;
          const template = saved.template || {};
          const fromCompany = Boolean(template.id) && !['system', 'default'].includes(template.template_type);
          setForm(formFromSnapshot(baseForm, saved.offer));
          setTemplateSource(fromCompany ? 'company' : 'system');
          setTemplateChoice(fromCompany ? String(template.id) : `system:${template.system_template_key || 'standard_full_time'}`);
          setDraftReady(true);
          setActiveStep(3);
        }
      } catch (error) {
        if (active) {
          setContext(null);
          toast.error(error.message || 'Could not load offer details.');
        }
      } finally {
        if (active) setLoadingContext(false);
      }
    };
    load();
    return () => { active = false; };
    // The modal is reset only when it is opened for another candidate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, applicationId]);

  // Load plan entitlements to lock/unlock offer-letter generation.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    (async () => {
      try {
        const cid = localStorage.getItem('companyAccountId');
        const qs = cid ? `company_id=${encodeURIComponent(cid)}` : '';
        const res = await fetch(getApiUrl(`/api/entitlements/me?${qs}`), { credentials: 'include' });
        const data = await res.json();
        if (!active) return;
        setOfferLocked(Boolean(data?.enforced) && data?.features?.offer_letter === false);
        setPlanName(data?.plan_name || '');
        setOfferCost(data?.test_costs?.OFFER_LETTER_GENERATED ?? null);
      } catch {
        if (active) { setOfferLocked(false); }   // fail-open: never block on error
      }
    })();
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    const offerDate = toDateInputValue(context?.offer_letter_date);
    if (!offerDate) return;
    setForm((current) => {
      if (current.template_fields?.offer_date) return current;
      return {
        ...current,
        template_fields: { ...current.template_fields, offer_date: offerDate },
      };
    });
  }, [context?.offer_letter_date]);

  useEffect(() => {
    if (!isStaticPdfTemplate) {
      setStaticPdfMappings([]);
      return;
    }
    const mappings = Array.isArray(staticPdfBlock?.field_mappings) ? staticPdfBlock.field_mappings : [];
    setStaticPdfMappings(mappings);
    setMappingDraft((current) => ({ ...current, page: String(staticPdfBlock?.pages?.[0]?.page || 1) }));
  }, [isStaticPdfTemplate, selectedTemplate?.id, selectedTemplate?.updated_at, staticPdfBlock]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const updateField = (section, field, value) => {
    setForm((current) => ({ ...current, [section]: { ...current[section], [field]: value } }));
    clearError(`${section}.${field}`);
    markDraftChanged();
  };

  const updateTemplateField = (field, value) => {
    setForm((current) => ({
      ...current,
      template_fields: { ...current.template_fields, [field]: value },
    }));
    clearError(`template_fields.${field}`);
    markDraftChanged();
  };

  const chooseTemplate = (choice) => {
    if (busy) return;
    const nextTemplate = [...systemTemplates, ...templates]
      .find((template) => String(template.id) === String(choice));
    setTemplateChoice(choice);
    const nextSource = nextTemplate?.type === 'company' || nextTemplate?.template_type ? 'company' : 'system';
    setTemplateSource(nextSource);
    if (nextTemplate?.key === 'part_time') {
      setForm((current) => ({
        ...current,
        employment: { ...current.employment, employment_type: 'Part-time' },
      }));
    }
    if (nextSource === 'system') setShowTemplateManager(false);
    resetTemplateFile();
    setTemplateName('');
    setFieldErrors({});
    markDraftChanged();
  };

  const chooseTemplateSource = (source) => {
    if (busy) return;
    const nextTemplate = source === 'company' ? templates[0] : (systemTemplates[0] || SYSTEM_TEMPLATE_FALLBACK[0]);
    setTemplateSource(source);
    if (source === 'system') setShowTemplateManager(false);
    setTemplateChoice(nextTemplate ? String(nextTemplate.id) : '');
    resetTemplateFile();
    setFieldErrors({});
    markDraftChanged();
  };

  const requestDraft = async () => {
    if (!selectedTemplate || !hasSelectedTemplate) throw new Error('Select an offer template before continuing.');
    const template = isCompanyTemplate
      ? { type: 'company', id: selectedTemplate.id }
      : { type: 'system', key: selectedTemplate.key };
    const response = await fetch(
      getApiUrl(`/api/hiring-lifecycle/applications/${applicationId}/offer-draft`),
      {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ offer: form, template }),
      },
    );
    const data = await response.json();
    if (!response.ok) {
      if (data?.details && typeof data.details === 'object') setFieldErrors(data.details);
      throw new Error(Object.values(data?.details || {})[0] || data.error || 'Could not save the offer draft.');
    }
    setFieldErrors({});
    setDraftReady(true);
    return data;
  };

  const openPdfPreview = async (url, kind) => {
    const response = await fetch(getApiUrl(url), { credentials: 'include' });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Could not open the PDF preview.');
    }
    const documentBlob = await response.blob();
    clearPreview();
    setPreviewUrl(URL.createObjectURL(documentBlob));
    setPreviewKind(kind);
  };

  const previewOffer = async () => {
    if (!context || !canGenerateSelectedTemplate || busy) return;
    setOperation('previewing');
    try {
      // With approvals on, an unchanged draft is shown again rather than saved again.
      const unchanged = approvalsOn && draftReady;
      if (!unchanged) await requestDraft();
      await openPdfPreview(`/api/hiring-lifecycle/applications/${applicationId}/offer-draft`, 'offer');
      setActiveStep(3);
      if (!unchanged) toast.success('Offer draft saved and ready for review.');
    } catch (error) {
      toast.error(error.message || 'Could not preview the offer letter.');
    } finally {
      setOperation('');
    }
  };

  const downloadDraft = async () => {
    if (!draftReady || busy) return;
    setOperation('downloading');
    try {
      const response = await fetch(getApiUrl(`/api/hiring-lifecycle/applications/${applicationId}/offer-draft`), { credentials: 'include' });
      if (!response.ok) throw new Error('Could not download the offer PDF.');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildOfferPdfFilename(form, context);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || 'Could not download the offer PDF.');
    } finally {
      setOperation('');
    }
  };

  const releaseOffer = async () => {
    if (!draftReady || busy) return;
    setOperation('releasing');
    try {
      const response = await fetch(
        getApiUrl(`/api/hiring-lifecycle/applications/${applicationId}/release-offer`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not release the offer.');
      toast.success(data.message || 'Offer released and emailed to the candidate.');
      clearPreview();
      if (onReleased) await onReleased(data);
    } catch (error) {
      toast.error(error.message || 'Could not release the offer.');
      refreshApproval();
    } finally {
      setOperation('');
    }
  };

  const uploadTemplate = async (replace = false) => {
    if (!templateFile || busy) {
      toast.error('Choose a DOCX, HTML, or fillable PDF company template first.');
      return;
    }
    if (replace && !isCompanyTemplate) {
      toast.error('Select the company template you want to replace first.');
      return;
    }
    setOperation(replace ? 'replacing-template' : 'uploading-template');
    try {
      const formData = new FormData();
      formData.append('template', templateFile);
      if (templateName.trim()) formData.append('name', templateName.trim());
      formData.append('category', templateCategory);
      const replacementTarget = replace ? selectedTemplate : null;
      const url = replace && replacementTarget
        ? `/api/hiring-lifecycle/offer-templates/${replacementTarget.id}/replace`
        : '/api/hiring-lifecycle/offer-templates';
      const response = await fetch(getApiUrl(url), { method: 'POST', credentials: 'include', body: formData });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not upload the offer template.');
      setTemplates((current) => (
        replace
          ? [data.template, ...current.filter((template) => String(template.id) !== String(replacementTarget.id))]
          : [data.template, ...current]
      ));
      // The company template itself remains blank and reusable. Selecting it
      // here only tells this offer draft which reusable format HR wants to use.
      setTemplateChoice(String(data.template.id));
      setTemplateSource('company');
      resetTemplateFile();
      setTemplateName('');
      setTemplateCategory('General');
      markDraftChanged();
      toast.success(data.message || 'Company template saved.');
    } catch (error) {
      toast.error(error.message || 'Could not upload the offer template.');
    } finally {
      setOperation('');
    }
  };

  const previewTemplate = async () => {
    if (!isCompanyTemplate || busy) return;
    setOperation('previewing-template');
    try {
      await openPdfPreview(`/api/hiring-lifecycle/offer-templates/${selectedTemplate.id}/preview`, 'template');
    } catch (error) {
      toast.error(error.message || 'Could not preview the company template.');
    } finally {
      setOperation('');
    }
  };

  const removeTemplate = async () => {
    if (!isCompanyTemplate || busy) return;
    setOperation('removing-template');
    try {
      const response = await fetch(
        getApiUrl(`/api/hiring-lifecycle/offer-templates/${selectedTemplate.id}`),
        { method: 'DELETE', credentials: 'include' },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not remove the company template.');
      setTemplates((current) => current.filter((template) => String(template.id) !== String(selectedTemplate.id)));
      setTemplateChoice('system:standard_full_time');
      setTemplateSource('system');
      setStaticPdfMappings([]);
      clearPreview();
      markDraftChanged();
      toast.success(data.message || 'Company template removed.');
    } catch (error) {
      toast.error(error.message || 'Could not remove the company template.');
    } finally {
      setOperation('');
    }
  };

  const updateStaticPdfMapping = (index, key, value) => {
    setStaticPdfMappings((current) => current.map((mapping, mappingIndex) => (
      mappingIndex === index ? { ...mapping, [key]: value } : mapping
    )));
    markDraftChanged();
  };

  const removeStaticPdfMapping = (index) => {
    setStaticPdfMappings((current) => current.filter((_, mappingIndex) => mappingIndex !== index));
    markDraftChanged();
  };

  const addStaticPdfMapping = () => {
    if (!mappingDraft.field) {
      toast.error('All available offer fields have already been mapped.');
      return;
    }
    if (staticPdfMappings.some((mapping) => mapping.field === mappingDraft.field)) {
      toast.error('This offer field is already mapped.');
      return;
    }
    const nextMapping = { ...mappingDraft };
    setStaticPdfMappings((current) => [...current, nextMapping]);
    const nextField = remainingStaticPdfFields.find((field) => field !== mappingDraft.field) || '';
    setMappingDraft((current) => ({ ...current, field: nextField }));
    markDraftChanged();
  };

  const saveStaticPdfMappings = async () => {
    if (!isStaticPdfTemplate || busy) return;
    setOperation('saving-mapping');
    try {
      const response = await fetch(
        getApiUrl(`/api/hiring-lifecycle/offer-templates/${selectedTemplate.id}/field-mappings`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ mappings: staticPdfMappings }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save the PDF field positions.');
      setTemplates((current) => current.map((template) => (
        String(template.id) === String(data.template.id) ? data.template : template
      )));
      const savedMappings = data.template?.blocks?.find((block) => block?.type === 'static_pdf')?.field_mappings || [];
      setStaticPdfMappings(savedMappings);
      markDraftChanged();
      toast.success(data.message || 'PDF field positions saved.');
    } catch (error) {
      toast.error(error.message || 'Could not save the PDF field positions.');
    } finally {
      setOperation('');
    }
  };

  const closeModal = () => {
    if (busy) return;
    clearPreview();
    setContext(null);
    setForm(emptyForm());
    setFieldErrors({});
    setDraftReady(false);
    resetTemplateFile();
    setTemplateName('');
    setTemplateCategory('General');
    setTemplateChoice('system:standard_full_time');
    setTemplateSource('system');
    setActiveStep(1);
    setShowTemplateManager(false);
    setStaticPdfMappings([]);
    onClose();
  };

  const continueToOfferDetails = () => {
    if (!hasSelectedTemplate) {
      toast.error('Select an offer template before continuing.');
      return;
    }
    setActiveStep(2);
  };

  const editOfferDetails = () => {
    clearPreview();
    setActiveStep(2);
  };

  const renderTextInput = (section, field, label, { required = false, type = 'text', placeholder = '', min, step } = {}) => (
    <div className="space-y-2" key={`${section}-${field}`}>
      <Label htmlFor={`${section}-${field}`}>{label}{required && <span className="text-destructive"> *</span>}</Label>
      <Input id={`${section}-${field}`} type={type} min={min} step={step} value={form[section][field]}
        onChange={(event) => updateField(section, field, event.target.value)} placeholder={placeholder}
        aria-invalid={Boolean(fieldError(fieldErrors, `${section}.${field}`))}
        className={fieldError(fieldErrors, `${section}.${field}`) ? 'border-destructive focus-visible:ring-destructive' : ''} disabled={busy} />
      <ErrorText message={fieldError(fieldErrors, `${section}.${field}`)} />
    </div>
  );

  const renderTemplateField = (field) => {
    const details = TEMPLATE_FIELDS[field];
    if (!details) return null;
    const errorKey = `template_fields.${field}`;
    const contextErrorKeys = {
      candidate_name: 'candidate.name', candidate_email: 'candidate.email',
      job_title: 'job.title', company_name: 'company.name', company_email: 'company.email',
    };
    const error = fieldError(fieldErrors, errorKey) || fieldError(fieldErrors, contextErrorKeys[field]);
    return (
      <div className="space-y-2" key={field}>
        <Label htmlFor={`template-${field}`}>{details.label} <span className="text-destructive">*</span></Label>
        {details.select ? (
          <Select value={form.template_fields[field] || ''} onValueChange={(value) => updateTemplateField(field, value)} disabled={busy}>
            <SelectTrigger aria-invalid={Boolean(error)} className={error ? 'border-destructive focus-visible:ring-destructive' : ''}><SelectValue placeholder={`Select ${details.label.toLowerCase()}`} /></SelectTrigger>
            <SelectContent>{details.select.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
          </Select>
        ) : details.multiline ? (
          <Textarea id={`template-${field}`} value={form.template_fields[field] || ''}
            onChange={(event) => updateTemplateField(field, event.target.value)} className={error ? 'border-destructive focus-visible:ring-destructive' : ''}
            disabled={busy} rows={field === 'salary_breakdown' ? 4 : 3} />
        ) : (
          <Input id={`template-${field}`} type={details.type || 'text'} min={details.min} step={details.step}
            value={form.template_fields[field] || ''} onChange={(event) => updateTemplateField(field, event.target.value)}
            className={error ? 'border-destructive focus-visible:ring-destructive' : ''} disabled={busy} />
        )}
        <ErrorText message={error} />
      </div>
    );
  };

  const renderCustomTemplateFields = () => GROUPS.map((group) => {
    const fields = templatePlaceholders.filter(
      (field) => TEMPLATE_FIELDS[field]?.group === group
        && !TEMPLATE_FIELDS[field]?.systemControlled
        && !COMMON_OFFER_TEMPLATE_FIELDS.has(field),
    );
    if (!fields.length) return null;
    return (
      <section className="space-y-4" key={group}>
        <h3 className="text-base font-semibold">{group}</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{fields.map(renderTemplateField)}</div>
      </section>
    );
  });

  const renderSystemTemplateField = (field) => {
    const error = fieldError(fieldErrors, `template_fields.${field.key}`);
    return (
      <div className="space-y-2" key={field.key}>
        <Label htmlFor={`system-template-${field.key}`}>{field.label}{field.required && <span className="text-destructive"> *</span>}</Label>
        {field.multiline ? (
          <Textarea id={`system-template-${field.key}`} value={form.template_fields[field.key] || ''} rows={3}
            onChange={(event) => updateTemplateField(field.key, event.target.value)} disabled={busy}
            className={error ? 'border-destructive focus-visible:ring-destructive' : ''} />
        ) : (
          <Input id={`system-template-${field.key}`} type={field.type || 'text'} min={field.min} max={field.max} step={field.step}
            placeholder={field.placeholder || ''} value={form.template_fields[field.key] || ''}
            onChange={(event) => updateTemplateField(field.key, event.target.value)} disabled={busy}
            className={error ? 'border-destructive focus-visible:ring-destructive' : ''} />
        )}
        <ErrorText message={error} />
      </div>
    );
  };

  const formatCompanyTemplateOption = (template) => template.name;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) closeModal(); }}>
      <DialogContent className="max-h-[92vh] max-w-5xl gap-5 overflow-y-auto p-5 sm:p-6">
        <DialogHeader className="space-y-1">
          <DialogTitle>{prefill ? 'Revise Offer Letter' : 'Create Offer Letter'}</DialogTitle>
          <DialogDescription>{prefill ? 'Update the details carried over from the previous offer, then review and re-send. The candidate must accept the revised offer.' : 'Choose a template, complete the offer details, then review and send.'}</DialogDescription>
        </DialogHeader>

        {offerLocked && (
          <PlanLockCard
            featureKey="offer_letter"
            featureLabel="Offer-letter generation"
            planName={planName}
            cost={offerCost}
          />
        )}

        {loadingContext ? (
          <div className="flex min-h-60 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading offer details...</div>
        ) : !context ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">Offer details could not be loaded. Close the dialog and try again.</div>
        ) : (
          <div className="space-y-7">
            {activeStep === 1 && <>
            <section className="space-y-4">
              <div><h3 className="text-base font-semibold">Choose a Template</h3></div>
              <div className="flex w-fit rounded-lg border bg-muted/30 p-1"><Button type="button" size="sm" variant={templateSource === 'system' ? 'default' : 'ghost'} onClick={() => chooseTemplateSource('system')} disabled={busy}>System Templates</Button><Button type="button" size="sm" variant={templateSource === 'company' ? 'default' : 'ghost'} onClick={() => chooseTemplateSource('company')} disabled={busy}>Company Templates</Button></div>
              <div className={templateSource === 'system' ? 'space-y-3 rounded-xl border bg-card p-4' : 'hidden'}><div><h4 className="text-sm font-semibold">System Templates</h4><p className="mt-1 text-xs text-muted-foreground">Read-only templates managed by SearchJob.ai.</p></div><div className="max-w-xl"><Select value={isSystemTemplate ? String(selectedTemplate.id) : undefined} onValueChange={chooseTemplate} disabled={busy}><SelectTrigger><SelectValue placeholder="Select a system template" /></SelectTrigger><SelectContent>{systemTemplates.map((template) => <SelectItem key={template.id} value={String(template.id)}>{template.name}</SelectItem>)}</SelectContent></Select></div>{selectedTemplateSummary && <p className="text-sm text-muted-foreground">{selectedTemplateSummary}</p>}</div>
              <div className={templateSource === 'company' ? 'space-y-3 rounded-xl border bg-card p-4' : 'hidden'}>
                <div><h4 className="text-sm font-semibold">Company Template Library</h4><p className="mt-1 text-xs text-muted-foreground">Reusable company-owned formats.</p></div>
                {templates.length ? <>
                  <div className="max-w-2xl">
                    <Select value={isCompanyTemplate ? String(selectedTemplate.id) : undefined} onValueChange={chooseTemplate} disabled={busy}>
                      <SelectTrigger><SelectValue placeholder="Choose a company template" /></SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => <SelectItem key={template.id} value={String(template.id)}>{formatCompanyTemplateOption(template)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {isCompanyTemplate && <p className="text-sm text-muted-foreground">{selectedTemplate.category || 'General'} · {selectedTemplate.template_type?.toUpperCase() || 'Template'} · {selectedTemplate.placeholders?.length || 0} {selectedTemplate.template_type === 'pdf' ? 'fillable fields' : 'placeholders'}</p>}
                </> : <div className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">No company templates yet. Open the template manager to add one.</div>}
              </div>
              {templateSource === 'company' && <><div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4"><p className="text-sm text-muted-foreground">Add, replace, preview, or remove reusable company templates separately.</p><Button type="button" variant="outline" onClick={() => setShowTemplateManager((current) => !current)} disabled={busy}>{showTemplateManager ? 'Hide Template Manager' : 'Manage Company Templates'}</Button></div>
              {showTemplateManager && <Card className="border-dashed bg-muted/[0.02]"><CardContent className="space-y-3 p-4">
                <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center"><div><h4 className="font-medium">Company Template Manager</h4><p className="text-xs text-muted-foreground">Add reusable DOCX, HTML, or fillable PDF templates. No fixed library limit.</p></div>
                  {isCompanyTemplate && <div className="grid w-full grid-cols-1 gap-2 sm:w-[490px] sm:grid-cols-2"><Button type="button" variant="outline" size="sm" className="h-10 w-full justify-center" onClick={(event) => { event.stopPropagation(); previewTemplate(); }} disabled={busy}><Eye className="mr-2 h-4 w-4" />Preview Template</Button><Button type="button" variant="outline" size="sm" className="h-10 w-full justify-center" onClick={(event) => { event.stopPropagation(); removeTemplate(); }} disabled={busy}><Trash2 className="mr-2 h-4 w-4" />Remove Template</Button></div>}
                </div>
                <div className={`grid grid-cols-1 gap-3 md:grid-cols-2 ${isCompanyTemplate ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
                  <div className="flex h-10 min-w-0 items-center rounded-md border bg-background">
                    <input ref={templateFileInputRef} type="file" className="sr-only" tabIndex={-1}
                      accept=".docx,.html,.htm,.pdf,text/html,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleTemplateFileChange} disabled={busy} />
                    <Button type="button" variant="ghost" className="h-full shrink-0" onClick={() => templateFileInputRef.current?.click()} disabled={busy}>Choose File</Button>
                    {templateFile?.name && <span className="min-w-0 truncate px-2 text-sm text-muted-foreground" title={templateFile.name}>{templateFile.name}</span>}
                  </div>
                  <Input value={templateName} onChange={(event) => setTemplateName(event.target.value)} placeholder="Template name (optional)" disabled={busy} />
                  <Select value={templateCategory} onValueChange={setTemplateCategory} disabled={busy}><SelectTrigger><SelectValue placeholder="Template category" /></SelectTrigger><SelectContent>{TEMPLATE_CATEGORIES.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent></Select>
                  <Button type="button" variant="outline" className="h-10 w-full" onClick={() => uploadTemplate(false)} disabled={busy || !templateFile}>
                    {operation === 'uploading-template' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Add Template
                  </Button>{isCompanyTemplate && <Button type="button" variant="outline" className="h-10 w-full" onClick={() => uploadTemplate(true)} disabled={busy || !templateFile}>
                    {operation === 'replacing-template' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Replace Selected
                  </Button>}
                </div>
              </CardContent></Card>}</>}
            </section>
            </>}

            {activeStep === 2 && <>
              <section className="space-y-3"><div><h3 className="text-base font-semibold">Candidate Details</h3><p className="mt-1 text-sm text-muted-foreground">Review the information pulled from the candidate, job, and company profile.</p></div>
              <Card className="border-slate-200 bg-slate-50/60"><CardContent className="p-4"><div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                <ReadOnlyValue label="Candidate Name" value={candidateDetails.name} /><ReadOnlyValue label="Candidate Email" value={candidateDetails.email} /><ReadOnlyValue label="Candidate Phone" value={candidateDetails.phone} />
                <ReadOnlyValue label="Job Title" value={job.title} />
                <ReadOnlyValue label="Company Name" value={company.name} /><ReadOnlyValue label="Company Address" value={company.address} />
                {job.department && <ReadOnlyValue label="Department" value={job.department} />}
              </div></CardContent></Card></section>

              <section className="space-y-4"><div><h3 className="text-base font-semibold">Offer Details</h3><p className="mt-1 text-sm text-muted-foreground">Complete the information that will appear in the offer letter.</p>{generatedOfferId && <p className="mt-2 text-sm text-muted-foreground">Offer ID: <span className="font-medium text-foreground">{generatedOfferId}</span></p>}</div><div className="grid grid-cols-1 gap-4 rounded-lg border bg-muted/[0.03] p-4 sm:grid-cols-2 sm:p-5">
                <div className="space-y-2"><Label>Salutation <span className="text-destructive">*</span></Label><Select value={form.template_fields.salutation || ''} onValueChange={(value) => updateField('template_fields', 'salutation', value)} disabled={busy}><SelectTrigger><SelectValue placeholder="Select salutation" /></SelectTrigger><SelectContent>{SALUTATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                {missingProfileFields.map(renderTemplateField)}
                {renderTextInput('template_fields', 'offer_date', 'Offer Letter Date', { required: true, type: 'date' })}
                {renderTextInput('employment', 'joining_date', 'Joining Date', { required: true, type: 'date' })}
                {renderTextInput('employment', 'work_location', 'Work Location', { required: true, placeholder: 'Bengaluru, India' })}
                <div className="space-y-2"><Label>Employment Type <span className="text-destructive">*</span></Label><Select value={form.employment.employment_type} onValueChange={(value) => updateField('employment', 'employment_type', value)} disabled={busy}><SelectTrigger aria-invalid={Boolean(fieldError(fieldErrors, 'employment.employment_type'))}><SelectValue placeholder="Select employment type" /></SelectTrigger><SelectContent>{EMPLOYMENT_TYPES.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent></Select><ErrorText message={fieldError(fieldErrors, 'employment.employment_type')} /></div>
                <div className="space-y-2"><Label>Work Arrangement <span className="text-destructive">*</span></Label><Select value={form.employment.work_arrangement} onValueChange={(value) => updateField('employment', 'work_arrangement', value)} disabled={busy}><SelectTrigger aria-invalid={Boolean(fieldError(fieldErrors, 'employment.work_arrangement'))}><SelectValue placeholder="Select work arrangement" /></SelectTrigger><SelectContent>{WORK_ARRANGEMENTS.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent></Select><ErrorText message={fieldError(fieldErrors, 'employment.work_arrangement')} /></div>
                {renderTextInput('employment', 'reporting_manager', 'Reporting To', { required: true, placeholder: 'Manager name' })}
                {renderTextInput('employment', 'probation_period', 'Probation Period', { required: true, placeholder: 'For example: 6 months' })}
                {renderTextInput('employment', 'offer_expiry_date', 'Offer Expiry Date', { required: true, type: 'date' })}
                <div className="space-y-2"><Label>Currency <span className="text-destructive">*</span></Label><Select value={form.compensation.currency} onValueChange={(value) => updateField('compensation', 'currency', value)} disabled={busy}><SelectTrigger aria-invalid={Boolean(fieldError(fieldErrors, 'compensation.currency'))}><SelectValue placeholder="Select currency" /></SelectTrigger><SelectContent>{CURRENCIES.map((currency) => <SelectItem key={currency.value} value={currency.value}>{currency.label}</SelectItem>)}</SelectContent></Select><ErrorText message={fieldError(fieldErrors, 'compensation.currency')} /></div>
                {renderTextInput('compensation', 'total_ctc', 'Total CTC', { required: true, type: 'number', min: '0.01', step: '0.01' })}
                {renderTextInput('compensation', 'variable_pay', 'Performance / Variable Pay (optional)', { type: 'number', min: '0', step: '0.01', placeholder: 'Annual maximum, e.g. 20000' })}
                {renderTextInput('signatory', 'name', 'HR / Authorized Person Name', { required: true, placeholder: 'Authorized representative' })}
                {renderTextInput('signatory', 'designation', 'Designation', { required: true, placeholder: 'For example: Head of Human Resources' })}
              </div></section>
            </>}

            {activeStep === 2 && isStaticPdfTemplate && <section className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 text-sm text-amber-950">
              <h3 className="font-semibold">Fillable PDF Required</h3>
              <p className="mt-1 leading-6">This older PDF does not use named form fields. Replace it with a fillable PDF containing fields such as <code>candidate_name</code>, <code>joining_date</code>, and <code>annual_ctc</code> to use the automatic offer flow. Existing released offers are unchanged.</p>
            </section>}

            {activeStep === 2 && showLegacyStaticPdfMapping && isStaticPdfTemplate && <section className="space-y-4 rounded-lg border bg-muted/[0.03] p-4 sm:p-5">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                <div>
                  <h3 className="text-base font-semibold">Map PDF Fields</h3>
                  <p className="mt-1 text-sm text-muted-foreground">This keeps the uploaded PDF design unchanged. Offer Details are collected separately; map only the values that should print on this template.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={previewTemplate} disabled={busy}><Eye className="mr-2 h-4 w-4" />Preview Template</Button>
              </div>

              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                {staticPdfPages.map((page) => <span key={page.page} className="rounded-full border bg-background px-2.5 py-1">Page {page.page}: {page.width_mm} × {page.height_mm} mm</span>)}
              </div>

              {staticPdfMappings.length ? (
                <div className="space-y-3">
                  {staticPdfMappings.map((mapping, index) => (
                    <Card key={`${mapping.field}-${index}`} className="bg-background"><CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_88px_1fr_1fr_1fr_1fr_72px_auto] lg:items-end">
                      <div className="space-y-1.5"><Label className="text-xs">Field</Label><Select value={mapping.field} onValueChange={(value) => updateStaticPdfMapping(index, 'field', value)} disabled={busy}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.keys(TEMPLATE_FIELDS).filter((field) => field === mapping.field || !staticPdfMappings.some((item) => item.field === field)).map((field) => <SelectItem key={field} value={field}>{TEMPLATE_FIELDS[field].label}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1.5"><Label className="text-xs">Page</Label><Select value={String(mapping.page)} onValueChange={(value) => updateStaticPdfMapping(index, 'page', value)} disabled={busy}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{staticPdfPages.map((page) => <SelectItem key={page.page} value={String(page.page)}>{page.page}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1.5"><Label className="text-xs">Left (mm)</Label><Input type="number" min="0" step="0.1" value={mapping.left_mm} onChange={(event) => updateStaticPdfMapping(index, 'left_mm', event.target.value)} disabled={busy} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Top (mm)</Label><Input type="number" min="0" step="0.1" value={mapping.top_mm} onChange={(event) => updateStaticPdfMapping(index, 'top_mm', event.target.value)} disabled={busy} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Width (mm)</Label><Input type="number" min="1" step="0.1" value={mapping.width_mm} onChange={(event) => updateStaticPdfMapping(index, 'width_mm', event.target.value)} disabled={busy} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Height (mm)</Label><Input type="number" min="1" step="0.1" value={mapping.height_mm} onChange={(event) => updateStaticPdfMapping(index, 'height_mm', event.target.value)} disabled={busy} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Font</Label><Input type="number" min="6" max="20" step="1" value={mapping.font_size} onChange={(event) => updateStaticPdfMapping(index, 'font_size', event.target.value)} disabled={busy} /></div>
                      <Button type="button" variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => removeStaticPdfMapping(index)} disabled={busy} aria-label={`Remove ${TEMPLATE_FIELDS[mapping.field]?.label || 'field'} mapping`}><Trash2 className="h-4 w-4" /></Button>
                    </CardContent></Card>
                  ))}
                </div>
              ) : <div className="rounded-md border border-dashed bg-background px-3 py-3 text-sm text-muted-foreground">No fields are mapped yet. Add the fields that should appear on the company PDF.</div>}

              {remainingStaticPdfFields.length > 0 ? <Card className="border-dashed bg-background"><CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_88px_1fr_1fr_1fr_1fr_72px_auto] lg:items-end">
                <div className="space-y-1.5"><Label className="text-xs">Add Field</Label><Select value={mappingDraft.field} onValueChange={(value) => setMappingDraft((current) => ({ ...current, field: value }))} disabled={busy}><SelectTrigger><SelectValue placeholder="Select a field" /></SelectTrigger><SelectContent>{remainingStaticPdfFields.map((field) => <SelectItem key={field} value={field}>{TEMPLATE_FIELDS[field].label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Page</Label><Select value={mappingDraft.page} onValueChange={(value) => setMappingDraft((current) => ({ ...current, page: value }))} disabled={busy}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{staticPdfPages.map((page) => <SelectItem key={page.page} value={String(page.page)}>{page.page}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Left (mm)</Label><Input type="number" min="0" step="0.1" value={mappingDraft.left_mm} onChange={(event) => setMappingDraft((current) => ({ ...current, left_mm: event.target.value }))} disabled={busy} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Top (mm)</Label><Input type="number" min="0" step="0.1" value={mappingDraft.top_mm} onChange={(event) => setMappingDraft((current) => ({ ...current, top_mm: event.target.value }))} disabled={busy} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Width (mm)</Label><Input type="number" min="1" step="0.1" value={mappingDraft.width_mm} onChange={(event) => setMappingDraft((current) => ({ ...current, width_mm: event.target.value }))} disabled={busy} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Height (mm)</Label><Input type="number" min="1" step="0.1" value={mappingDraft.height_mm} onChange={(event) => setMappingDraft((current) => ({ ...current, height_mm: event.target.value }))} disabled={busy} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Font</Label><Input type="number" min="6" max="20" step="1" value={mappingDraft.font_size} onChange={(event) => setMappingDraft((current) => ({ ...current, font_size: event.target.value }))} disabled={busy} /></div>
                <Button type="button" variant="outline" onClick={addStaticPdfMapping} disabled={busy}>Add</Button>
              </CardContent></Card> : <p className="text-sm text-muted-foreground">All available offer fields are mapped.</p>}

              <div className="flex justify-end"><Button type="button" onClick={saveStaticPdfMappings} disabled={busy || !staticPdfMappings.length}>{operation === 'saving-mapping' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{operation === 'saving-mapping' ? 'Saving Positions...' : 'Save Field Positions'}</Button></div>
            </section>}

            {activeStep === 2 && isSystemTemplate && systemTemplateFields.length > 0 && <section className="space-y-4"><div><h3 className="text-base font-semibold">{selectedTemplate.name} Details</h3><p className="mt-1 text-sm text-muted-foreground">Complete the details required only for this template.</p></div><div className="grid grid-cols-1 gap-4 rounded-lg border bg-muted/[0.03] p-4 sm:grid-cols-2 sm:p-5">{systemTemplateFields.map(renderSystemTemplateField)}</div></section>}

            {activeStep === 2 && isCompanyTemplate && (!isStaticPdfTemplate || templatePlaceholders.length > 0) && renderCustomTemplateFields()}

            {previewUrl && ((activeStep === 3 && previewKind === 'offer') || (activeStep === 1 && previewKind === 'template')) && <section className="space-y-3 rounded-lg border bg-muted/10 p-3"><div className="flex items-center justify-between gap-3"><div><h3 className="font-semibold">{previewKind === 'template' ? 'Company Template Preview' : 'Offer Letter Preview'}</h3><p className="text-sm text-muted-foreground">{previewKind === 'template' ? 'This is the reusable blank company template. Candidate details are added only when HR creates an offer.' : 'Review the draft, then edit it or release the offer.'}</p></div><Button variant="outline" size="sm" onClick={previewKind === 'offer' ? editOfferDetails : clearPreview} disabled={busy}>{previewKind === 'offer' ? <><Pencil className="mr-2 h-4 w-4" />Edit Offer Details</> : 'Hide Preview'}</Button></div><iframe title="Offer letter PDF preview" src={previewUrl} className="h-[560px] w-full rounded border bg-white" /></section>}
          </div>
        )}

        {activeStep === 3 && <OfferApprovalNote approval={approval} />}

        <DialogFooter className="gap-2 sm:flex-wrap sm:gap-2 sm:space-x-0">
          <Button type="button" variant="outline" onClick={closeModal} disabled={busy}>Cancel</Button>
          {activeStep === 1 && (offerLocked
            ? <Button type="button" disabled variant="outline"><Lock className="mr-2 h-4 w-4" />Not in your plan</Button>
            : <Button type="button" onClick={continueToOfferDetails} disabled={!context || !hasSelectedTemplate || busy}>Continue to Offer Details</Button>)}
          {activeStep === 2 && <><Button type="button" variant="outline" onClick={() => setActiveStep(1)} disabled={busy}>Back</Button><Button type="button" onClick={previewOffer} disabled={!context || !canGenerateSelectedTemplate || busy || offerLocked}>{operation === 'previewing' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}{operation === 'previewing' ? 'Preparing Preview...' : 'Review Offer Letter'}</Button></>}
          {activeStep === 3 && <>{!offerLocked && offerCost != null && approvalAllowsRelease(approval) && (<span className="mr-auto self-center text-xs text-muted-foreground">Releasing deducts {offerCost} credit{offerCost === 1 ? '' : 's'}</span>)}<Button type="button" variant="outline" onClick={editOfferDetails} disabled={busy}><Pencil className="mr-2 h-4 w-4" />Edit Details</Button><Button type="button" variant="outline" onClick={downloadDraft} disabled={!draftReady || busy}>{operation === 'downloading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Download PDF</Button><OfferReleaseActions approval={approval} onApprovalChanged={refreshApproval} applicationId={applicationId} draftReady={draftReady} busy={busy} offerLocked={offerLocked} releasing={operation === 'releasing'} onRelease={releaseOffer} /></>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
